import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, ThumbsUp, ThumbsDown } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useFeedback } from './FeedbackContext'
import { submitFeedback } from './feedback-api'
import { getLastAnalyticsEvent, track, AnalyticsEvents } from '@/lib/services/analytics'
import { FEEDBACK_SURVEYS, type FeedbackSurveyDef } from './feedback-surveys'
import { FEEDBACK_MANUAL_LANE_VAR } from './feedback-occlusion'

type Phase = 'score' | 'followup' | 'submitting' | 'done' | 'error'

const COOLDOWN_KEY = 'feedback_dismissed_at'
const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000

export function isFeedbackCoolingDown(): boolean {
  try {
    const raw = localStorage.getItem(COOLDOWN_KEY)
    if (!raw) return false
    return Date.now() - Number(raw) < COOLDOWN_MS
  } catch {
    return false
  }
}

function setDismissCooldown() {
  try {
    localStorage.setItem(COOLDOWN_KEY, String(Date.now()))
  } catch { /* noop */ }
}

export function FeedbackToast() {
  const { isOpen, openOptions, sessionId, close, pageIds, cornerOccupied } = useFeedback()
  const { t } = useTranslation(['app', 'common'])
  const [phase, setPhase] = useState<Phase>('score')
  const [score, setScore] = useState<number | null>(null)
  const [openText, setOpenText] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const sessionIdRef = useRef(sessionId)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  sessionIdRef.current = sessionId

  function clearCloseTimer() {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const surveyKey = openOptions?.surveyKey ?? 'generic_feedback'
  const survey: FeedbackSurveyDef = FEEDBACK_SURVEYS[surveyKey] ?? FEEDBACK_SURVEYS.generic_feedback
  const triggerType = openOptions?.triggerType ?? 'manual'
  const triggerEvent = openOptions?.triggerEvent ?? null
  // Auto prompts must not sit on a chart canvas or a chat composer.
  // The same session keeps its draft while parked and shows it again when
  // the corner is free. A new open() (the feedback menu) replaces that
  // session instead of reusing its answers.
  const parked = cornerOccupied && triggerType === 'auto'
  // A menu card on that surface used to be fixed at the top center, which
  // covers the Survey data counts line, Analyst content, and the twin's
  // first message. It now takes a strip above the page. The page shrinks
  // by this strip's height so those regions start below the card.
  const lane = isOpen && cornerOccupied && !parked

  const commonProps = {
    survey_key: surveyKey,
    trigger_type: triggerType,
    trigger_event: triggerEvent,
    page_url: window.location.pathname,
  }

  useLayoutEffect(() => {
    clearCloseTimer()
    if (!isOpen || sessionId == null) return
    setPhase('score')
    setScore(null)
    setOpenText('')
    track(AnalyticsEvents.FEEDBACK_SHOWN, commonProps)
    // Reset only when a new session opens. Parking keeps this session id,
    // so an in-progress score and note stay with that auto prompt.
    // A close timer from an older submission is dropped with the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionId])

  useEffect(() => {
    return () => clearCloseTimer()
  }, [])

  useLayoutEffect(() => {
    const root = document.documentElement
    if (!lane) {
      root.style.setProperty(FEEDBACK_MANUAL_LANE_VAR, '0px')
      return
    }
    const node = containerRef.current
    if (!node) return
    function publish() {
      root.style.setProperty(FEEDBACK_MANUAL_LANE_VAR, `${node?.offsetHeight ?? 0}px`)
    }
    publish()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish)
    observer?.observe(node)
    return () => {
      observer?.disconnect()
      root.style.setProperty(FEEDBACK_MANUAL_LANE_VAR, '0px')
    }
  }, [lane, phase])

  useEffect(() => {
    if (phase === 'followup') {
      textareaRef.current?.focus()
    }
  }, [phase])

  useEffect(() => {
    if (!isOpen || parked) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, parked, score])

  const handleDismiss = useCallback(() => {
    track(AnalyticsEvents.FEEDBACK_DISMISSED, {
      ...commonProps,
      had_score: score !== null,
    })
    if (triggerType === 'auto') {
      setDismissCooldown()
    }
    close()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [close, score, triggerType])

  const handleScoreSelect = (value: number) => {
    setScore(value)
    setPhase('followup')
    track(AnalyticsEvents.FEEDBACK_SCORE_SELECTED, {
      ...commonProps,
      score: value,
    })
  }

  const handleSubmit = async () => {
    if (score === null || sessionId == null) return
    const submittingSessionId = sessionId
    const submittingScore = score
    const submittingText = openText.trim()
    setPhase('submitting')

    const lastEvent = getLastAnalyticsEvent()
    const context: Record<string, unknown> = {
      pageUrl: window.location.pathname,
      triggerType,
      triggerEvent,
      ...pageIds,
    }
    if (lastEvent) {
      context.lastEvent = lastEvent
    }

    function isStaleSubmission() {
      return sessionIdRef.current !== submittingSessionId
    }

    try {
      await submitFeedback({
        surveyKey,
        score: submittingScore,
        openText: submittingText || undefined,
        context,
      })
      if (isStaleSubmission()) return
      track(AnalyticsEvents.FEEDBACK_SUBMITTED, {
        ...commonProps,
        score: submittingScore,
        has_open_text: submittingText.length > 0,
      })
      setPhase('done')
      clearCloseTimer()
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null
        if (isStaleSubmission()) return
        close()
      }, 2000)
    } catch {
      if (isStaleSubmission()) return
      setPhase('error')
    }
  }

  if (!isOpen || parked) return null

  const thumbOptions: { value: number; icon: typeof ThumbsUp; label: string }[] = [
    { value: 1, icon: ThumbsDown, label: t('chromeA11y.thumbsDown') },
    { value: 5, icon: ThumbsUp, label: t('chromeA11y.thumbsUp') },
  ]

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-label={t('chromeA11y.feedback')}
      data-testid="feedback-toast"
      data-feedback-anchor={lane ? 'lane' : 'corner'}
      className={lane ? 'px-4 pt-4 pb-2' : 'fixed bottom-6 right-6 z-50 w-[320px]'}
    >
    <div
      className={cn(
        lane && 'mx-auto w-[320px]',
        'rounded-xl shadow-2xl border backdrop-blur-xl',
        lane
          ? 'animate-in slide-in-from-top-2 fade-in duration-300'
          : 'animate-in slide-in-from-bottom-4 fade-in duration-300',
        'bg-card/90 text-foreground border-border',
        'dark:bg-card/90 dark:border-border dark:text-foreground dark:shadow-[0px_15px_30px_rgba(0,0,0,0.35)]',
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <span className="text-sm font-medium dark:text-white/90">{survey.label}</span>
        <button
          onClick={handleDismiss}
          data-testid="feedback-toast-dismiss"
          aria-label={t('chromeA11y.dismissFeedback')}
          className="p-1 rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="px-4 pb-4">
        {phase === 'done' ? (
          <p
            data-testid="feedback-toast-thanks"
            className="text-sm py-3 text-center opacity-60 dark:text-white/60"
          >
            {t('feedback.thanks')}
          </p>
        ) : phase === 'error' ? (
          <div data-testid="feedback-toast-error" className="py-3 text-center">
            <p className="text-sm text-destructive mb-2">{t('somethingWrong', { ns: 'common' })}</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPhase('followup')}
              data-testid="feedback-toast-retry"
            >
              {t('tryAgain', { ns: 'common' })}
            </Button>
          </div>
        ) : (
          <>
            {/* Thumbs up / down */}
            <div className="flex gap-3 mb-3" data-testid="feedback-toast-scores">
              {thumbOptions.map(({ value, icon: Icon, label }) => (
                <button
                  key={value}
                  onClick={() => handleScoreSelect(value)}
                  data-testid={`feedback-toast-score-${value}`}
                  aria-label={label}
                  aria-pressed={score === value}
                  className={cn(
                    'group/thumb flex-1 flex items-center justify-center h-11 rounded-lg text-sm font-medium transition-all duration-200',
                    'border',
                    score === value
                      ? 'bg-foreground border-foreground text-background shadow-sm'
                      : cn(
                          'bg-background/60 border-border text-foreground hover:border-foreground/35 hover:bg-muted hover:shadow-sm',
                          'dark:bg-background/40 dark:border-border dark:text-foreground dark:hover:border-foreground/35 dark:hover:bg-muted/60',
                        ),
                  )}
                >
                  <Icon
                    className={cn(
                      'h-5 w-5 transition-transform duration-200',
                      'group-hover/thumb:scale-110',
                      score === value && 'scale-110',
                    )}
                    strokeWidth={1.5}
                  />
                </button>
              ))}
            </div>

            {/* Followup area */}
            {(phase === 'followup' || phase === 'submitting') && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                <textarea
                  ref={textareaRef}
                  value={openText}
                  onChange={(e) => setOpenText(e.target.value)}
                  placeholder={survey.followupPrompt}
                  data-testid="feedback-toast-textarea"
                  rows={3}
                  className={cn(
                    'w-full rounded-lg border px-3 py-2 text-sm resize-none',
                    'bg-background/60 border-border text-foreground placeholder:text-muted-foreground',
                    'focus:outline-none focus:ring-2 focus:ring-ring/35 focus:ring-offset-1 focus:ring-offset-transparent',
                    'dark:bg-background/40 dark:border-border dark:text-foreground dark:placeholder:text-muted-foreground',
                    'dark:focus:ring-ring/35 dark:focus:ring-offset-background',
                  )}
                />
                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    onClick={handleSubmit}
                    disabled={phase === 'submitting'}
                    data-testid="feedback-toast-submit"
                    className="bg-foreground text-background hover:bg-foreground/90"
                  >
                    {phase === 'submitting' ? t('sending', { ns: 'common' }) : t('submit', { ns: 'common' })}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </div>
  )
}
